import type { PlaygroundProjectFile } from "../auth/api-client";

/** Client-side mirror of the API limits in services/api/src/handlers/playground-projects.ts. */
export const PLAYGROUND_MAX_FILES = 50;
export const PLAYGROUND_MAX_FILE_BYTES = 200 * 1024;
export const PLAYGROUND_MAX_TOTAL_BYTES = 2 * 1024 * 1024;

const PATH_SEGMENT_RE = /^[A-Za-z0-9._ -]+$/;

export function validatePlaygroundFilePath(path: string): string | null {
  const p = path.trim();
  if (!p) return "File name is required.";
  if (p.length > 256) return "File name is too long.";
  if (p.startsWith("/")) return "Use a relative path (no leading /).";
  if (p.includes("\\")) return "Use forward slashes in paths.";
  for (const seg of p.split("/")) {
    if (!seg) return "Path has an empty segment.";
    if (seg === "." || seg === "..") return "Paths may not contain '.' or '..' segments.";
    if (!PATH_SEGMENT_RE.test(seg)) {
      return "Use letters, digits, '.', '_', '-', and spaces only.";
    }
  }
  return null;
}

export function validatePlaygroundFileSet(files: readonly PlaygroundProjectFile[]): string | null {
  if (files.length > PLAYGROUND_MAX_FILES) return `Too many files (max ${PLAYGROUND_MAX_FILES}).`;
  let total = 0;
  for (const f of files) {
    const bytes = new TextEncoder().encode(f.content).byteLength;
    if (bytes > PLAYGROUND_MAX_FILE_BYTES) {
      return `${f.path} is too large (max ${Math.floor(PLAYGROUND_MAX_FILE_BYTES / 1024)} KB per file).`;
    }
    total += bytes;
  }
  if (total > PLAYGROUND_MAX_TOTAL_BYTES) {
    return `Project is too large (max ${Math.floor(PLAYGROUND_MAX_TOTAL_BYTES / (1024 * 1024))} MB total).`;
  }
  return null;
}

/** Decodes an uploaded file as strict UTF-8 text; returns null when it is not a text file. */
export async function readUploadAsText(file: File): Promise<string | null> {
  const buf = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buf);
  } catch {
    return null;
  }
}

export function validatePlaygroundFolderPath(path: string): string | null {
  const p = path.trim().replace(/^\/+|\/+$/g, "");
  if (!p) return "Folder name is required.";
  if (p.endsWith(".py")) return "Folder names should not end in .py.";
  return validatePlaygroundFilePath(p);
}

export function sortProjectFiles(files: readonly PlaygroundProjectFile[]): PlaygroundProjectFile[] {
  return [...files].sort((a, b) => {
    if (a.path === "main.py") return -1;
    if (b.path === "main.py") return 1;
    return a.path.localeCompare(b.path);
  });
}
